import { api } from "@lib/orpc-client";
import { client } from "@lib/api";
import { useState, useEffect } from "react";
import { useMerchantStore } from "@stores/merchantStore";
import * as Sentry from "@sentry/react";

export type MerchantInformationParams = {
  business_name: string;
  email: string;
};

export type MerchantInformationResponse = {
  business_name: string;
  created_at: string;
  email: string;
  image_url: string;
  public_address: string;
  uuid: string;
};

export type ImageUploadResponse = {
  image_url: string;
  uuid: string;
};

export const useManageMerchant = () => {
  const {
    name,
    email,
    profilePictureUrl,
    updateMerchantInfo,
    setProfilePictureUrl,
  } = useMerchantStore();
  const [loading, setLoading] = useState(false);
  const [uploadImageLoading, setUploadImageLoading] = useState(false);
  const [updateMerchantInfoLoading, setUpdateMerchantInfoLoading] =
    useState(false);

  const [merchantInformation, setMerchantInformation] = useState<{
    name: string;
    email: string;
    profilePictureUrl: string;
  }>({
    name,
    email,
    profilePictureUrl,
  });
  const [merchantInformationLoading, setMerchantInformationLoading] =
    useState(false);

  const getMerchantInformation = async () => {
    try {
      setMerchantInformationLoading(true);
      // If merchant data exists in the store, return it without fetching
      if (name) {
        return;
      }
      // If no local data, try to fetch from API
      const response = await api.wallet.merchant.get({});

      // Update the Zustand store with the fetched data
      if (response) {
        updateMerchantInfo({
          name: response.business_name,
          email: response.email,
          profilePictureUrl: response.image_url,
        });
      }

      setMerchantInformation({
        name: response.business_name,
        email: response.email,
        profilePictureUrl: response.image_url,
      });
    } catch (error) {
      setMerchantInformation({
        name: "",
        email: "",
        profilePictureUrl: "",
      });
    } finally {
      setMerchantInformationLoading(false);
    }
  };

  useEffect(() => {
    getMerchantInformation();
  }, []);

  // Effect to track overall loading state
  useEffect(() => {
    if (
      !updateMerchantInfoLoading &&
      !uploadImageLoading &&
      !merchantInformationLoading
    ) {
      setLoading(false);
    } else {
      setLoading(true);
    }
  }, [
    updateMerchantInfoLoading,
    uploadImageLoading,
    merchantInformationLoading,
  ]);

  const uploadMerchantImage = async (imageData: string | File) => {
    try {
      setUploadImageLoading(true);

      // Create a proper FormData object
      const formData = new FormData();

      // Handle both base64 string and File object
      if (typeof imageData === "string" && imageData.startsWith("data:")) {
        // Convert base64 to blob
        const response = await fetch(imageData);
        const blob = await response.blob();
        formData.append("image", blob, "profile-image.jpg");
      } else if (imageData instanceof File) {
        formData.append("image", imageData);
      } else {
        throw new Error("Invalid image data provided");
      }

      // FormData multipart uploads bypass oRPC JSON client and use legacy fetch client.
      const response = await client.doPost<ImageUploadResponse>(
        "/wallet/merchant/image",
        formData,
        { Accept: "application/json" }
      );
      setProfilePictureUrl(response.image_url);
      return response;
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    } finally {
      setUploadImageLoading(false);
    }
  };

  const createMerchant = async (params: MerchantInformationParams) => {
    try {
      setUpdateMerchantInfoLoading(true);
      const response = await api.wallet.merchant.create(params);
      updateMerchantInfo({
        name: response.business_name,
        email: response.email,
        profilePictureUrl: response.image_url,
      });
      return response;
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    } finally {
      setUpdateMerchantInfoLoading(false);
    }
  };

  const updateMerchantInfoAPI = async (params: MerchantInformationParams) => {
    try {
      setUpdateMerchantInfoLoading(true);
      const response = await api.wallet.merchant.update(params);
      updateMerchantInfo({
        name: response.business_name,
        email: response.email,
        profilePictureUrl: response.image_url,
      });
      return response;
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    } finally {
      setUpdateMerchantInfoLoading(false);
    }
  };

  return {
    loading,
    uploadMerchantImage,
    createMerchant,
    updateMerchantInfo: updateMerchantInfoAPI,
    merchantInformation,
  };
};

export default useManageMerchant;
